import { ISearchItem, ISearchResults } from '@gn-audio/core/api/product-catalogue/models';
import { debounce } from 'src/Foundation/Design/platform/app/utils/debounce';
import { CustomEvent } from 'src/Foundation/Shared/platform/app/enums';

import { pageSize } from '../Constants';
import { Repeater } from './repeater';
import { TopNavigationSearchResults } from './search-results';

class CurrentSearchPhrase extends TopNavigationSearchResults {
  public selectedPage = 1;

  private list: Repeater<ISearchItem>;
  private pagination: HTMLElement;
  private globalSearchInterval: number;
  private currentSearchPhrase: string;
  private languagePrefix: string | undefined;

  constructor() {
    super();
    this.list = new Repeater<ISearchItem>('search-result-container');
    this.pagination = document.querySelector('#result__paging');

    const langInPath = document.documentElement.dataset.langInPath;

    if (langInPath && langInPath === "true") {
      this.languagePrefix = document.documentElement.dataset.marketLocale;
    }
  }

  get currentSearch(): string {
    return this.currentSearchPhrase;
  }

  set currentSearch(searchPhrase: string) {
    this.resetSelectedPage();
    this.currentSearchPhrase = searchPhrase.substr(0, 100);

    if (this.currentSearchPhrase && this.currentSearchPhrase.length > 2 && (window as any).ga !== undefined) {
      (window as any).ga('send', 'pageview', '/search?q=' + this.currentSearchPhrase);
    }

    const hasSearchPhrase = this.currentSearchPhrase.length !== 0;
    if (hasSearchPhrase) {
      this.updateSearchInterface();
    } else {
      this.searchContainerToggle('.site-search__top-content', true);
    }

    this.searchContainerToggle('.search-container', hasSearchPhrase);
  }

  set productResultsCount(counter: number) {
    const count: NodeListOf<Element> = document.querySelectorAll('.product-results-count');
    count.forEach((el: HTMLElement) => {
      el.innerHTML = `${counter}`;
    });
  }

  public changePage(prodPerPage: number, total: number) {
    const firstArrow: HTMLElement = document.querySelector('.paging-direction.first');
    const prevArrow: HTMLElement = document.querySelector('.paging-direction.prev');
    const nextArrow: HTMLElement = document.querySelector('.paging-direction.next');
    const lastArrow: HTMLElement = document.querySelector('.paging-direction.last');
    const totalPages: number = Math.ceil(total / prodPerPage);

    firstArrow.onclick = () => {
      this.resetSelectedPage();
      this.updateSearchInterface();
    }
    prevArrow.onclick = () => {
      this.selectedPage -= 1;
      this.updateSearchInterface();
    }
    nextArrow.onclick = () => {
      this.selectedPage += 1;
      this.updateSearchInterface();
    }
    lastArrow.onclick = () => {
      this.selectedPage = totalPages;
      this.updateSearchInterface();
    }
  }

  public updateSearchInterface() {
    if (this.globalSearchInterval) {
      clearInterval(this.globalSearchInterval);
    }

    this.searchContainerToggle('.site-search__results__preloader', true);

    this.updateProductSearch(this.currentSearchPhrase, this.selectedPage)
      .then((data: ISearchResults<ISearchItem> | null) => {
        if (data === null) {
          this.searchContainerToggle('.site-search__results__preloader');
          this.searchContainerToggle('.search-result__products__container');
          this.searchContainerToggle('.search-result__nothing', true);
          this.searchContainerToggle('.site-search__top-content', true);
          return;
        }

        this.searchContainerToggle('.search-result__products__container', true);
        this.list.createElement(data.items, this.languagePrefix);
        this.createPaging(this.selectedPage || 1, pageSize, data.count);
        this.searchContainerToggle('.search-result__nothing', data.count === 0);
        this.searchContainerToggle('.site-search__top-content', data.count === 0);

        this.productResultsCount = data.count;
        this.searchContainerToggle('.site-search__results__preloader');
        this.changePage(pageSize, data.count);
      });
  }

  public createPaging(page: number, prodPerPage: number, total: number) {
    this.selectedPage = page;
    const totalPages: number = Math.ceil(total / prodPerPage);
    const productFrom = page * pageSize - (pageSize - 1);
    const productTo = (page === totalPages) ? total : page * pageSize;

    this.pagination.innerHTML = `${productFrom} - ${productTo} of ${total}`;

    const arrowElements: NodeListOf<Element> = document.querySelectorAll('.paging-direction');
    arrowElements.forEach(elem => elem.classList.remove('disabled'));

    if (page === 1) {
      const beforeArrows: NodeListOf<Element> = document.querySelectorAll('.paging-direction.first, .paging-direction.prev');
      beforeArrows.forEach(elem => elem.classList.add('disabled'));
    }
    if (page === totalPages) {
      const afterArrows: NodeListOf<Element> = document.querySelectorAll('.paging-direction.last, .paging-direction.next');
      afterArrows.forEach(elem => elem.classList.add('disabled'));
    }
  }

  private resetSelectedPage() {
    this.selectedPage = 1;
  }
}

window.addEventListener(CustomEvent.ApiConfigured, () => {
  const searchField: HTMLInputElement = document.getElementById('q') as HTMLInputElement;

  if (searchField === null) {
    return;
  }

  const getParameterByName = (name: string, url?: string): string => {
    if (!url) {
      url = window.location.href;
    }

    name = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);

    if (!results) {
      return undefined;
    }

    if (!results[2]) {
      return '';
    }

    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  };
  const queryFromUrl = getParameterByName('q');

  const search: CurrentSearchPhrase = new CurrentSearchPhrase();

  if (searchField.value) {
    search.currentSearch = searchField.value;
  }

  if (queryFromUrl) {
    search.currentSearch = queryFromUrl;
    search.searchContainerToggle('.search-result__confirmation', true);
    searchField.value = queryFromUrl;
    const confirmation = document.querySelector('.search-result__confirmation-clean');

    confirmation.addEventListener('click', () => {
      searchField.value = '';
      search.currentSearch = '';
    });
  }

  searchField.addEventListener('input', debounce(() => {
    search.currentSearch = searchField.value;
  }, 1000));
});
